import React from "react";
import { Building2, RefreshCw, Trash2, Users, AlertTriangle, Check } from "lucide-react";
import { useGet, apiPatch, apiPost, apiDelete } from "../../hooks/useApi.js";
import { cn } from "@hts/ui";

type Tenant = {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  _count: { memberships: number; integrationConnections: number; ticketCache: number };
};

type Membership = {
  userId: string;
  tenantId: string;
  role: string;
  user: { id: string; email: string; name: string | null; deactivated: boolean };
};

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  admin: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  accounting: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  dispatcher: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  tech: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  viewer: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
};

export function WorkspacesPage() {
  const { data, loading, error, refetch } = useGet<{ items: Tenant[] }>("/api/v1/tenants");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const tenants = data?.items ?? [];
  const selected = tenants.find((t) => t.id === selectedId);

  async function rename(id: string) {
    if (!editingName.trim()) return;
    setBusy(true); setErr("");
    try { await apiPatch("/api/v1/tenants/" + id, { name: editingName.trim() }); refetch(); setEditingName(""); }
    catch (e) { setErr(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  async function suspend(id: string) {
    if (!confirm("Suspend this workspace? Disconnects all integrations. No data is deleted.")) return;
    setBusy(true); setErr("");
    try { await apiPost("/api/v1/tenants/" + id + "/suspend", {}); refetch(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  async function reactivate(id: string) {
    setBusy(true); setErr("");
    try { await apiPost("/api/v1/tenants/" + id + "/reactivate", {}); refetch(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  async function removeMember(tenantId: string, userId: string) {
    if (!confirm("Remove this user from the workspace?")) return;
    setBusy(true); setErr("");
    try { await apiDelete("/api/v1/tenants/" + tenantId + "/memberships/" + userId); }
    catch (e) { setErr(e instanceof Error ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  if (loading) return <p role="status">Loading workspaces…</p>;
  if (error) return <p role="alert">Could not load workspaces ({error}).</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[hsl(var(--content))]">Workspaces</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))]">Manage all tenant workspaces in the system.</p>
      </div>
      {err && <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{err}</p>}

      <div className="grid gap-3">
        {tenants.map((t) => (
          <div key={t.id} className={cn("rounded-xl border p-4", t.status === "suspended" ? "border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/10" : "border-[hsl(var(--border))] bg-[hsl(var(--surface))]")}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--surface-subtle))]">
                  <Building2 className="h-5 w-5 text-[hsl(var(--content-subtle))]" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[hsl(var(--content))]">{t.name}</h3>
                    {t.status === "suspended" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        <AlertTriangle className="h-3 w-3" /> Suspended
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        <Check className="h-3 w-3" /> Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[hsl(var(--content-subtle))]">/{t.slug} · {t._count.memberships} users · {t._count.integrationConnections} integrations · {t._count.ticketCache} tickets</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {t.status === "suspended" ? (
                  <button disabled={busy} onClick={() => reactivate(t.id)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/30">
                    <RefreshCw className="h-3.5 w-3.5" /> Reactivate
                  </button>
                ) : (
                  <button disabled={busy} onClick={() => suspend(t.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/30">
                    Suspend
                  </button>
                )}
                <button onClick={() => { setSelectedId(t.id === selectedId ? null : t.id); setEditingName(t.name); }} className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]">
                  {selectedId === t.id ? "Close" : "Manage"}
                </button>
              </div>
            </div>
            {selectedId === t.id && (
              <div className="mt-4 space-y-4 border-t border-[hsl(var(--border))] pt-4">
                {/* Rename */}
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="block text-xs font-medium text-[hsl(var(--content-subtle))] mb-1">Workspace name</span>
                    <input value={editingName} onChange={(e) => setEditingName(e.target.value)} className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500" />
                  </label>
                  <button disabled={busy || !editingName.trim()} onClick={() => rename(t.id)} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Save</button>
                </div>
                {/* Memberships */}
                <Memberships tenantId={t.id} onRemove={(uid) => removeMember(t.id, uid)} busy={busy} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Memberships({ tenantId, onRemove, busy }: { tenantId: string; onRemove: (userId: string) => void; busy: boolean }) {
  const { data, loading, error } = useGet<{ items: Membership[] }>("/api/v1/tenants/" + tenantId + "/memberships");
  if (loading) return <p className="text-xs text-[hsl(var(--content-subtle))]">Loading members…</p>;
  if (error) return <p className="text-xs text-red-600">Could not load members.</p>;
  const members = data?.items ?? [];
  if (members.length === 0) return <p className="text-xs text-[hsl(var(--content-subtle))]">No members.</p>;
  return (
    <div>
      <p className="mb-2 flex items-center gap-1 text-xs font-medium text-[hsl(var(--content-subtle))]"><Users className="h-3 w-3" /> Members ({members.length})</p>
      <div className="space-y-1">
        {members.map((m) => (
          <div key={m.userId} className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[hsl(var(--content))]">{m.user.name || m.user.email}</span>
              {m.user.deactivated && <span className="text-xs text-red-500">deactivated</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", ROLE_COLORS[m.role] ?? "")}>{m.role}</span>
              <button disabled={busy || m.role === "owner"} onClick={() => onRemove(m.userId)} className="text-[hsl(var(--content-subtle))] hover:text-red-600 disabled:opacity-30" title={m.role === "owner" ? "Cannot remove owner" : "Remove"}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}